import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { LowerCasePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ValidationFormComponent } from '../validation-form/validation-form.component';
import {
  AutoHttpsDirective,
  CopyToClipboardDirective,
  MaxValueDirective,
  NoLeadingSpaceDirective,
  NumberValueDirective,
  PositiveNumbersDirective,
  PrefixInputDirective,
} from '@core-utils';
import { AlertComponent, KeeniconComponent } from '../../partials';

const HINT_DISMISSED_PREFIX = 'ui_input_hint_dismissed_';

@Component({
  selector: 'ui-form-input',
  imports: [
    ReactiveFormsModule,
    ValidationFormComponent,
    NgxMaskDirective,
    NgClass,
    PositiveNumbersDirective,
    FormsModule,
    NoLeadingSpaceDirective,
    NumberValueDirective,
    NgTemplateOutlet,
    LowerCasePipe,
    KeeniconComponent,
    CopyToClipboardDirective,
    MaxValueDirective,
    AutoHttpsDirective,
    AlertComponent,
    PrefixInputDirective,
  ],
  templateUrl: './form-input.component.html',
  styleUrl: './form-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[attr.data-cy]': 'dataCy() || null' },
})
export class FormInputComponent {
  dataCy = input<string>('');
  control = input<AbstractControl>();
  global = input<boolean>(true);
  type = input<string>('text');
  placeholder = input<string>('');
  mask = input<string>('');
  class = input<string>('');
  skipValidation = input<boolean>(false);
  enableAutoHttps = input<boolean>(false);
  maxValue = input<number>(Number.MAX_SAFE_INTEGER);
  backgroundImageView = input<boolean>(true);
  noNegative = input<boolean>(false);
  isNumber = input<boolean>(false);
  value = model<string | number | null>('');
  description = input<string>('');
  lockedMessage = input<string>('');
  suffix = input<string>('');
  inputPrefix = input<string>('');
  copyBtn = input<boolean>(false);
  readonly = input<boolean>(false);
  disabled = input<boolean>(false);
  alertMessage = input<string>('');
  isLoading = input<boolean>(false);
  tooltip = input<string>('');
  tooltipKey = input<string>('');

  hasTyped = signal<boolean>(false);
  dismissedByUser = signal<boolean>(false);

  storageKey = computed<string>(() => {
    const key = this.tooltipKey() || this.tooltip();
    return HINT_DISMISSED_PREFIX + btoa(encodeURIComponent(key)).slice(0, 32);
  });

  showHint = computed<boolean>(
    () => !!this.tooltip() && this.hasTyped() && !this.dismissedByUser(),
  );

  constructor() {
    effect(() => {
      const key = this.storageKey();
      if (key) {
        this.dismissedByUser.set(localStorage.getItem(key) !== null);
      }
    });
  }

  private inputElement =
    viewChild<ElementRef<HTMLInputElement>>('inputElement');

  get formControl(): FormControl {
    return this.control() as FormControl;
  }

  focus(): void {
    this.inputElement()?.nativeElement?.focus();
  }

  onInputActivity(): void {
    if (!this.hasTyped()) {
      this.hasTyped.set(true);
    }
  }

  dismissHint(): void {
    this.dismissedByUser.set(true);
    localStorage.setItem(this.storageKey(), '');
  }
}
